import React, { useEffect, useRef, useState, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

interface ScrollGradientContainerProps {
    children: React.ReactNode;
    maxHeight?: string;
    scrollBarClass?: string;
    fadeClass?: string;
    className?: string;
}

const ScrollGradientContainer: React.FC<ScrollGradientContainerProps> = ({ children, maxHeight, fadeClass, className, scrollBarClass = 'scrollbar-floating-thumb' }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [showTopGradient, setShowTopGradient] = useState(false);
    const [showBottomGradient, setShowBottomGradient] = useState(false);

    const checkScroll = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        setShowTopGradient(scrollTop > 0);
        setShowBottomGradient(scrollTop + clientHeight < scrollHeight);
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        checkScroll();

        container.addEventListener('scroll', checkScroll);

        const resizeObserver = new ResizeObserver(checkScroll);
        resizeObserver.observe(container);

        return () => {
            container.removeEventListener('scroll', checkScroll);
            resizeObserver.disconnect();
        };
    }, [checkScroll]);

    return (
        <div className={`relative ${className}`}>
            <div className={twMerge(`pointer-events-none z-[1] absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-[#f4f8fb] to-transparent transition-opacity duration-500 ${!showTopGradient && 'opacity-0'}`, fadeClass)} />
            <div className={twMerge(`pointer-events-none z-[1] absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#f4f8fb] to-transparent transition-opacity duration-500 ${!showBottomGradient && 'opacity-0'}`, fadeClass)} />

            <div ref={containerRef} style={{ maxHeight }} className={twMerge(`overflow-y-auto overflow-x-hidden`, scrollBarClass)}>
                {children}
            </div>
        </div>
    );
};

export default ScrollGradientContainer;

